/**
 * NDJSON (Newline-Delimited JSON) parser.
 *
 * Parses streaming NDJSON responses using brace counting for robust
 * JSON object boundary detection, handling partial chunks correctly.
 *
 * Replaces the standalone ndjson-parser.ts with a class-based parser
 * that can be used by the unified StreamClient.
 */

/**
 * Incrementally parses NDJSON-formatted text and yields complete JSON objects.
 *
 * Uses brace counting instead of newline splitting to handle JSON objects
 * that contain embedded newlines (e.g. in string values). Correctly handles
 * escape sequences and string literals.
 */
export class NDJSONParser<T> {
  private buffer = '';

  /**
   * Feeds a text chunk into the parser and returns any complete objects.
   *
   * @param chunk - Raw text from the stream (may contain partial JSON)
   * @returns Array of parsed objects (may be empty)
   */
  push(chunk: string): T[] {
    this.buffer += chunk;
    return this.extractObjects();
  }

  /**
   * Flushes any remaining data in the buffer.
   * Call this when the stream ends to capture the last object.
   *
   * @returns Array of parsed objects from remaining buffer (0 or 1)
   */
  flush(): T[] {
    const trimmed = this.buffer.trim();
    if (!trimmed) return [];

    try {
      const obj = JSON.parse(trimmed) as T;
      this.buffer = '';
      return [obj];
    } catch {
      this.buffer = '';
      return [];
    }
  }

  /**
   * Resets internal buffer state.
   */
  reset(): void {
    this.buffer = '';
  }

  /**
   * Extracts complete JSON objects from the buffer using brace counting.
   * Handles nested braces, string literals, and escape sequences.
   */
  private extractObjects(): T[] {
    const results: T[] = [];
    let startIdx = 0;
    let braceCount = 0;
    let inString = false;
    let escapeNext = false;

    for (let i = 0; i < this.buffer.length; i++) {
      const char = this.buffer[i];

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\') {
        escapeNext = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (inString) continue;

      if (char === '{') {
        if (braceCount === 0) {
          startIdx = i;
        }
        braceCount++;
      } else if (char === '}') {
        braceCount--;

        if (braceCount === 0) {
          const jsonStr = this.buffer.substring(startIdx, i + 1);
          try {
            results.push(JSON.parse(jsonStr) as T);
          } catch {
            // Malformed object — skip
          }
          // Advance buffer past this object
          this.buffer = this.buffer.substring(i + 1);
          // Reset scan position to start of new buffer
          i = -1;
          startIdx = 0;
        }
      }
    }

    return results;
  }
}
